export const LICENCE_TYPES = [
    { type: 'assessment', label: 'Assessment', plural: 'Assessments' },
    { type: 'course', label: 'Course', plural: 'Courses' },
    { type: 'apprenticeship', label: 'Apprenticeship', plural: 'Apprenticeships' },
];

const typeMeta = LICENCE_TYPES.reduce((items, item) => {
    items[item.type] = item;
    return items;
}, {});

export const getLicenceTypeLabel = (type, plural = false) => {
    const meta = typeMeta[type];
    if (!meta) return type || '';
    return plural ? meta.plural : meta.label;
};

export const makeLicenceKey = (type, id) => `${type}:${id}`;

export const parseLicenceKey = (key) => {
    const value = String(key || '');
    const index = value.indexOf(':');
    if (index < 0) return { type: '', id: value };
    return { type: value.slice(0, index), id: value.slice(index + 1) };
};

const toCount = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

export const normalizeLicence = (licence) => {
    const type = licence.type || parseLicenceKey(licence.key).type;
    const id = String(licence.id ?? parseLicenceKey(licence.key).id);
    const allocated = toCount(licence.allocated);
    const assigned = toCount(licence.assigned);
    const remaining = licence.remaining === undefined || licence.remaining === null
        ? allocated - assigned
        : toCount(licence.remaining);
    return {
        ...licence,
        key: licence.key || makeLicenceKey(type, id),
        type,
        id,
        name: licence.name || 'Untitled licence',
        allocated,
        assigned,
        remaining,
    };
};

export const normalizeUser = (user) => ({
    ...user,
    uuid: String(user.uuid),
    email: user.email || user.username || '',
    first_name: user.first_name || '',
    last_name: user.last_name || '',
});

/** The loaded matrix is what the user sees, so the selected tab uses its count; other tabs use the API total. */
export const projectUserCount = (project, loadedMatrix) => {
    if (loadedMatrix && Array.isArray(loadedMatrix.users)) return loadedMatrix.users.length;
    const total = Number(project?.total_users);
    return Number.isFinite(total) ? total : null;
};

export const getUserDisplayName = (user) => (
    [user?.first_name, user?.last_name].filter(Boolean).join(' ')
);

export const buildAssignmentMap = (licences = [], assignments = []) => {
    const map = {};
    licences.forEach((licence) => {
        map[licence.key] = new Set();
    });
    assignments.forEach(({ user_uuid: userId, licence_key: key }) => {
        if (map[key]) map[key].add(String(userId));
    });
    return map;
};

export const isAssigned = (map, key, userId) => Boolean(map && map[key] && map[key].has(userId));

export const getCellState = (baseline, draft, key, userId) => {
    const before = isAssigned(baseline, key, userId);
    const after = isAssigned(draft, key, userId);
    if (after && !before) return 'added';
    if (!after && before) return 'removed';
    return after ? 'assigned' : 'none';
};

const setSize = (map, key) => (map && map[key] ? map[key].size : 0);

export const getRemaining = (licence, baseline, draft) => (
    licence.remaining - (setSize(draft, licence.key) - setSize(baseline, licence.key))
);

export const getRemainingByKey = (licences, baseline, draft) => licences.reduce((items, licence) => {
    items[licence.key] = getRemaining(licence, baseline, draft);
    return items;
}, {});

/**
 * Applies assign/remove changes to the draft. Assignments stop at each
 * licence's remaining capacity; anything over the limit is reported in `blocked`.
 */
export const applyChanges = (draft, baseline, licences, changes = []) => {
    const licenceByKey = licences.reduce((items, licence) => {
        items[licence.key] = licence;
        return items;
    }, {});
    let next = draft;
    let applied = 0;
    const blocked = [];

    changes.forEach(({ key, userIds = [], assign }) => {
        const licence = licenceByKey[key];
        if (!licence) return;
        const updated = new Set(next[key] || []);
        let remaining = getRemaining(licence, baseline, next);
        let skipped = 0;
        let changed = false;

        userIds.forEach((rawId) => {
            const userId = String(rawId);
            if (assign) {
                if (updated.has(userId)) return;
                if (remaining <= 0) {
                    skipped += 1;
                    return;
                }
                updated.add(userId);
                remaining -= 1;
                applied += 1;
                changed = true;
            } else if (updated.delete(userId)) {
                applied += 1;
                changed = true;
            }
        });

        if (skipped) blocked.push({ key, name: licence.name, skipped });
        if (changed) next = { ...next, [key]: updated };
    });

    return { draft: next, applied, blocked };
};

export const toggleCell = (draft, baseline, licences, key, userId) => applyChanges(
    draft,
    baseline,
    licences,
    [{ key, userIds: [userId], assign: !isAssigned(draft, key, userId) }],
);

export const setColumn = (draft, baseline, licences, key, userIds, assign) => applyChanges(
    draft,
    baseline,
    licences,
    [{ key, userIds, assign }],
);

export const setRow = (draft, baseline, licences, userId, keys, assign) => applyChanges(
    draft,
    baseline,
    licences,
    keys.map(key => ({ key, userIds: [userId], assign })),
);

export const setMany = (draft, baseline, licences, keys, userIds, assign) => applyChanges(
    draft,
    baseline,
    licences,
    keys.map(key => ({ key, userIds, assign })),
);

export const computeDiff = (baseline, draft) => {
    const keys = new Set([...Object.keys(baseline || {}), ...Object.keys(draft || {})]);
    const add = [];
    const remove = [];
    let addedCount = 0;
    let removedCount = 0;

    keys.forEach((key) => {
        const before = baseline[key] || new Set();
        const after = draft[key] || new Set();
        const added = [...after].filter(id => !before.has(id));
        const removed = [...before].filter(id => !after.has(id));
        if (added.length) {
            add.push({ licence_key: key, user_uuids: added });
            addedCount += added.length;
        }
        if (removed.length) {
            remove.push({ licence_key: key, user_uuids: removed });
            removedCount += removed.length;
        }
    });

    return { add, remove, addedCount, removedCount };
};

export const isDiffEmpty = diff => !diff || (diff.addedCount === 0 && diff.removedCount === 0);

export const summarizeByType = (licences, remainingByKey) => LICENCE_TYPES.map(({ type, plural }) => {
    const items = licences.filter(licence => licence.type === type);
    return {
        type,
        label: plural,
        count: items.length,
        allocated: items.reduce((total, licence) => total + licence.allocated, 0),
        remaining: items.reduce((total, licence) => (
            total + Math.max(0, remainingByKey[licence.key] ?? licence.remaining)
        ), 0),
    };
});

const normalizeSearch = value => String(value || '').trim().toLowerCase();

export const filterLicences = (licences, { type = 'all', search = '' } = {}) => {
    const query = normalizeSearch(search);
    return licences.filter(licence => (
        (type === 'all' || licence.type === type)
        && (!query || licence.name.toLowerCase().includes(query))
    ));
};

export const filterUsers = (users, search = '') => {
    const query = normalizeSearch(search);
    if (!query) return users;
    return users.filter(user => (
        user.email.toLowerCase().includes(query)
        || getUserDisplayName(user).toLowerCase().includes(query)
    ));
};

export const countUserAssignments = (draft, userId) => Object.values(draft || {}).reduce(
    (total, users) => total + (users.has(userId) ? 1 : 0),
    0,
);
